import React, { useEffect, useMemo, useState } from 'react';
import { Trash2, AlertCircle, ArrowRight, TrendingDown, TrendingUp, Minus } from 'lucide-react';
import { Button } from '../../../../../components/ui/Button';
import { Card } from '../../../../../components/ui/Card';
import { ConfirmDialog } from '../../../../../components/shared/ConfirmDialog';
import { listSnapshots, loadSnapshot, deleteSnapshot, clearAllSnapshots, getSnapshotCap, diffSnapshots, type SnapshotIndexEntry, type SnapshotDiff } from '../../../../../governance/pipeline/snapshots';
import { severityColor, severityLabel } from '../../../../../governance/utils/severity';
import { Badge } from '../../../../../components/ui/Badge';
interface CompareSnapshotsTabProps {
  refreshTrigger: number;
}
export function CompareSnapshotsTab({
  refreshTrigger
}: CompareSnapshotsTabProps) {
  const [snapshots, setSnapshots] = useState<SnapshotIndexEntry[]>([]);
  const [baseId, setBaseId] = useState<string>('');
  const [targetId, setTargetId] = useState<string>('');
  const [diff, setDiff] = useState<SnapshotDiff | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
  const [pendingClearAll, setPendingClearAll] = useState(false);
  const cap = getSnapshotCap();
  useEffect(() => {
    const list = listSnapshots();
    setSnapshots(list);
    if (list.length >= 2 && !baseId && !targetId) {
      // Default to comparing the two most recent
      setTargetId(list[0].id);
      setBaseId(list[1].id);
    }
  }, [refreshTrigger]);
  const handleCompare = () => {
    setError(null);
    if (!baseId || !targetId) {
      setError('Selecteer twee snapshots om te vergelijken.');
      return;
    }
    if (baseId === targetId) {
      setError('Selecteer twee verschillende snapshots.');
      return;
    }
    const base = loadSnapshot(baseId);
    const target = loadSnapshot(targetId);
    if (!base || !target) {
      setError('Kon een of beide snapshots niet laden.');
      return;
    }
    // Ensure base is older than target for logical diffing
    const isBaseOlder = new Date(base.createdAt) < new Date(target.createdAt);
    const actualBase = isBaseOlder ? base : target;
    const actualTarget = isBaseOlder ? target : base;
    if (!isBaseOlder) {
      setBaseId(actualBase.id);
      setTargetId(actualTarget.id);
    }
    setDiff(diffSnapshots(actualBase, actualTarget));
  };
  const handleDelete = (id: string) => {
    setPendingDeleteId(id);
  };
  const confirmDelete = () => {
    const id = pendingDeleteId;
    if (!id) return;
    deleteSnapshot(id);
    setSnapshots(listSnapshots());
    if (baseId === id) setBaseId('');
    if (targetId === id) setTargetId('');
    if (diff?.baseId === id || diff?.targetId === id) setDiff(null);
    setPendingDeleteId(null);
  };
  const confirmClearAll = () => {
    clearAllSnapshots();
    setSnapshots([]);
    setBaseId('');
    setTargetId('');
    setDiff(null);
    setPendingClearAll(false);
  };
  const pendingSnapshot = pendingDeleteId ? snapshots.find(s => s.id === pendingDeleteId) : null;
  const formatDate = (iso: string) => {
    return new Date(iso).toLocaleString('nl-NL', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  return <div className="space-y-6" data-id="element-5295" data-mp="3jxflwk0dopaa">
      <Card className="p-5" data-id="element-5296" data-mp="3k0a8oi6v6m6v">
        <div className="flex flex-col md:flex-row gap-4 items-end" data-id="element-5297" data-mp="3ezmg3s2zlxaj">
          <div className="flex-1 w-full" data-id="element-5298" data-mp="3f0xjhqsit314">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-5299" data-mp="3a84d6yhelmmx">
              Base Snapshot (Ouder)
            </label>
            <select value={baseId} onChange={e => setBaseId(e.target.value)} className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500" data-id="element-5300" data-mp="359ulwyysv66l">
              
              <option value="" data-id="element-5301" data-mp="2r9s57g28aisx">-- Selecteer --</option>
              {snapshots.map(s => <option key={s.id} value={s.id} data-id="element-5302" data-mp="2rbmnsnq11ytu">
                  {formatDate(s.createdAt)} {s.label ? `- ${s.label}` : ''} (
                  {s.totalIssues} issues)
                </option>)}
            </select>
          </div>
          <div className="flex-1 w-full" data-id="element-5303" data-mp="lgu63ai3k7jx">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-5304" data-mp="lg2hwkqtxc3o">
              Target Snapshot (Nieuwer)
            </label>
            <select value={targetId} onChange={e => setTargetId(e.target.value)} className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500" data-id="element-5305" data-mp="bieaovc1ec4m">
              
              <option value="" data-id="element-5306" data-mp="2irh2vpjuam4">-- Selecteer --</option>
              {snapshots.map(s => <option key={s.id} value={s.id} data-id="element-5307" data-mp="7gayu5lgh5it">
                  {formatDate(s.createdAt)} {s.label ? `- ${s.label}` : ''} (
                  {s.totalIssues} issues)
                </option>)}
            </select>
          </div>
          <Button variant="primary" onClick={handleCompare} disabled={!baseId || !targetId} data-id="element-5308" data-mp="21tiycakdm5as">
            
            Vergelijk
          </Button>
        </div>

        {error && <div className="mt-4 p-3 bg-red-50 text-red-700 rounded-md flex items-center gap-2 text-sm" data-id="element-5309" data-mp="2707idyu3bg54">
            <AlertCircle className="w-4 h-4" data-id="element-5310" data-mp="2krccymilpi8l" />
            {error}
          </div>}

        <div className="mt-4 pt-4 border-t border-gray-100" data-id="element-5311" data-mp="2g0m6f5yfv9cb">
          <div className="flex items-center justify-between mb-2" data-id="element-5312" data-mp="2fz2rbioir4c4">
            <h4 className="text-sm font-medium text-gray-700" data-id="element-5313" data-mp="2uo7c97mp7mnx">
              Opgeslagen Snapshots ({snapshots.length}/{cap})
            </h4>
            {snapshots.length > 0 && <button onClick={() => setPendingClearAll(true)} className="text-xs font-medium text-gray-500 hover:text-red-600 transition-colors inline-flex items-center gap-1" data-id="element-5314" data-mp="33polc61ouo34">
              
                <Trash2 className="w-3.5 h-3.5" data-id="element-5315" data-mp="19bxd9az0cc9s" />
                Alles wissen
              </button>}
          </div>
          <div className="flex flex-wrap gap-2" data-id="element-5316" data-mp="15au04w8cguc9">
            {snapshots.map(s => <div key={s.id} className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-md px-3 py-1.5 text-sm" data-id="element-5317" data-mp="15cookkitr4l3">
              
                <span className="text-gray-600" data-id="element-5318" data-mp="39f81fby99heo">{formatDate(s.createdAt)}</span>
                {s.label && <span className="font-medium text-gray-900" data-id="element-5319" data-mp="3ed03gysm6jwl">{s.label}</span>}
                <button onClick={() => handleDelete(s.id)} className="text-gray-400 hover:text-red-600 transition-colors ml-1" title="Verwijderen" data-id="element-5320" data-mp="3jc3323ptrxx6">
                
                  <Trash2 className="w-3.5 h-3.5" data-id="element-5321" data-mp="3nbvdyn2oyg5p" />
                </button>
              </div>)}
            {snapshots.length === 0 && <span className="text-sm text-gray-500 italic" data-id="element-5322" data-mp="yd8ao79fbds">
                Geen snapshots gevonden.
              </span>}
          </div>
        </div>
      </Card>

      {diff && <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-5323" data-mp="5zj4wx41jisl">
          {/* Aggregate Deltas */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-5324" data-mp="ey4moq1outsb">
            <DeltaStat label="Totaal Issues" delta={diff.aggregate.totalIssues} data-id="element-5325" data-mp="eyeac6z9gouu" />
          
            <DeltaStat label="Blockers" delta={diff.aggregate.issuesByKind.blocker} data-id="element-5326" data-mp="1zhi45bmu5ojy" />
          
            <DeltaStat label="Coach" delta={diff.aggregate.issuesByKind.coach} data-id="element-5327" data-mp="1vd0o9njljveg" />
          
            <DeltaStat label="Info" delta={diff.aggregate.issuesByKind.info} data-id="element-5328" data-mp="1qg0qs10tc0yy" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-5329" data-mp="1lhpeljn1pedk">
            {/* Top Code Changes */}
            <Card className="p-5" data-id="element-5330" data-mp="2j3mud9a1j5lt">
              <h3 className="font-semibold text-gray-900 mb-4" data-id="element-5331" data-mp="2j5zcbmfstfyv">
                Code Verschuivingen
              </h3>
              <div className="space-y-2" data-id="element-5332" data-mp="29e377rfaopl5">
                {diff.codeChanges.filter(c => c.delta !== 0).slice(0, 10).map(c => <div key={c.code} className="flex items-center justify-between p-2 rounded-md bg-gray-50 border border-gray-100" data-id="element-5333" data-mp="24g1rngh2v8ef">
                
                      <span className="font-mono text-xs text-gray-700" data-id="element-5334" data-mp="31wqp6xyzvoi0">
                        {c.code}
                      </span>
                      <div className="flex items-center gap-3 text-sm" data-id="element-5335" data-mp="25gmdekhcnojg">
                        <span className="text-gray-500 w-8 text-right" data-id="element-5336" data-mp="25ejh5iplsz6a">
                          {c.base}
                        </span>
                        <ArrowRight className="w-3 h-3 text-gray-400" data-id="element-5337" data-mp="1wgh9ikbd78a1" />
                        <span className="font-medium text-gray-900 w-8" data-id="element-5338" data-mp="20h9jxgvz3j7w">
                          {c.target}
                        </span>
                        <span className={`w-12 text-right font-medium ${c.delta > 0 ? 'text-red-600' : 'text-emerald-600'}`} data-id="element-5339" data-mp="2o7x6976mybdz">
                    
                          {c.delta > 0 ? '+' : ''}
                          {c.delta}
                        </span>
                      </div>
                    </div>)}
                {diff.codeChanges.filter(c => c.delta !== 0).length === 0 && <p className="text-sm text-gray-500 italic" data-id="element-5340" data-mp="13qhbn5fa6vgo">
                    Geen wijzigingen in issue codes.
                  </p>}
              </div>
            </Card>

            {/* Verdict Flips */}
            <Card className="p-5" data-id="element-5341" data-mp="108bhbwh00ors">
              <h3 className="font-semibold text-gray-900 mb-4" data-id="element-5342" data-mp="1079uyvuj8fur">
                Verdict Flips ({diff.verdictChanges.length})
              </h3>
              <div className="space-y-2 max-h-[400px] overflow-y-auto pr-2" data-id="element-5343" data-mp="1a70pgxj3euuu">
                {diff.verdictChanges.map(v => <div key={v.qid} className="flex items-center justify-between p-2 rounded-md bg-gray-50 border border-gray-100" data-id="element-5344" data-mp="157o6invce7xj">
                
                    <span className="text-sm font-medium text-gray-700" data-id="element-5345" data-mp="hiv3k7gayztf">
                      {v.qid}
                    </span>
                    <div className="flex items-center gap-2 text-xs" data-id="element-5346" data-mp="r8ep5kveh7rz">
                      <VerdictBadge verdict={v.from} data-id="element-5347" data-mp="r9puvq8eoplq" />
                      <ArrowRight className="w-3 h-3 text-gray-400" data-id="element-5348" data-mp="mheqob6muhnd" />
                      <VerdictBadge verdict={v.to} data-id="element-5349" data-mp="3uxhyqi1nq8c8" />
                    </div>
                  </div>)}
                {diff.verdictChanges.length === 0 && <p className="text-sm text-gray-500 italic" data-id="element-5350" data-mp="3pxdjswgedlo4">
                    Geen vragen zijn van verdict veranderd.
                  </p>}
              </div>
            </Card>
          </div>
        </div>}

      <ConfirmDialog open={pendingDeleteId !== null} title="Snapshot verwijderen?" message={pendingSnapshot ? `Snapshot van ${formatDate(pendingSnapshot.createdAt)}${pendingSnapshot.label ? ` ("${pendingSnapshot.label}")` : ''} wordt definitief verwijderd. Dit kan niet ongedaan gemaakt worden.` : 'Deze snapshot wordt definitief verwijderd. Dit kan niet ongedaan gemaakt worden.'} confirmLabel="Verwijderen" cancelLabel="Annuleren" variant="danger" onConfirm={confirmDelete} onCancel={() => setPendingDeleteId(null)} data-id="element-5351" data-mp="1pbuh0kig7hez" />
      

      <ConfirmDialog open={pendingClearAll} title="Alle snapshots wissen?" message={`Alle ${snapshots.length} opgeslagen snapshots worden definitief verwijderd. Dit kan niet ongedaan gemaakt worden.`} confirmLabel="Alles wissen" cancelLabel="Annuleren" variant="danger" onConfirm={confirmClearAll} onCancel={() => setPendingClearAll(false)} data-id="element-5352" data-mp="11iuheucz3326" />
      
    </div>;
}
function DeltaStat({
  label,
  delta
}: {
  label: string;
  delta: number;
}) {
  const isPositive = delta > 0;
  const isNegative = delta < 0;
  const isZero = delta === 0;
  return <Card className="p-4 flex flex-col" data-id="element-5353" data-mp="388pia6o77vt9">
      <span className="text-sm text-gray-500 font-medium" data-id="element-5354" data-mp="38a0ixxmckng5">{label}</span>
      <div className="mt-2 flex items-center gap-2" data-id="element-5355" data-mp="3d791w0fbkvig">
        <span className={`text-2xl font-bold ${isZero ? 'text-gray-900' : isPositive ? 'text-red-600' : 'text-emerald-600'}`} data-id="element-5356" data-mp="3d5xwjw687fp8">
          
          {isPositive ? '+' : ''}
          {delta}
        </span>
        {!isZero && (isPositive ? <TrendingUp className="w-5 h-5 text-red-500" data-id="element-5357" data-mp="2pig37w7bc9k5" /> : <TrendingDown className="w-5 h-5 text-emerald-500" data-id="element-5358" data-mp="2pgmumbd53f9i" />)}
        {isZero && <Minus className="w-5 h-5 text-gray-400" data-id="element-5359" data-mp="2ua7uya27glbv" />}
      </div>
    </Card>;
}
function VerdictBadge({
  verdict
}: {
  verdict: string;
}) {
  const colors = {
    PASS: 'bg-emerald-100 text-emerald-800',
    FAIL: 'bg-red-100 text-red-800',
    REVIEW_NEEDED: 'bg-amber-100 text-amber-800'
  };
  return <span className={`px-2 py-0.5 rounded-full font-medium ${colors[verdict as keyof typeof colors] || 'bg-gray-100 text-gray-800'}`} data-id="element-5360" data-mp="1tr4nguu6fpv5">
      
      {verdict}
    </span>;
}